import { Component } from 'react'
import { captureException } from '@/lib/sentry'

export default class ErrorBoundary extends Component {
  state = { hasError: false, error: null, info: null, showDetails: false }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, info) {
    this.setState({ info })
    // Surface to console for desktop devtools and to localStorage so mobile
    // users can read back the last failure. captureException forwards to
    // Sentry when VITE_SENTRY_DSN is set (no-ops otherwise).
    console.error('[ErrorBoundary]', error, info)
    captureException(error, {
      contexts: { react: { componentStack: info?.componentStack } },
    })
    try {
      const payload = {
        message: error?.message || String(error),
        stack: error?.stack || null,
        componentStack: info?.componentStack || null,
        href: typeof window !== 'undefined' ? window.location.href : null,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        at: new Date().toISOString(),
      }
      localStorage.setItem('illuminate_last_error', JSON.stringify(payload))
    } catch {
      // localStorage can throw in private mode — non-fatal
    }
  }

  render() {
    if (this.state.hasError) {
      const { error, info, showDetails } = this.state
      const message = error?.message || 'Unknown error'
      const stack = error?.stack || ''
      const componentStack = info?.componentStack || ''
      return (
        <div className="min-h-screen flex flex-col items-center justify-center px-4 py-8 bg-[var(--bg-base,#0b1229)]">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-[var(--gold,#e9c349)] mb-4">
            Something went wrong
          </h1>
          <p className="text-[var(--text-primary,#dce1ff)]/70 text-center max-w-md mb-6">
            We're sorry — an unexpected error occurred. Please try refreshing the page.
          </p>
          <div className="flex flex-wrap gap-3 justify-center mb-6">
            <button
              onClick={() => window.location.reload()}
              className="rounded-full bg-[var(--gold,#e9c349)] px-6 py-3 font-semibold text-[var(--text-on-gold,#1a1a2e)] shadow-lg transition-all hover:brightness-110"
            >
              Refresh Page
            </button>
            <button
              onClick={() => {
                window.location.href = '/'
              }}
              className="rounded-full border border-[var(--text-primary,#dce1ff)]/30 px-6 py-3 font-semibold text-[var(--text-primary,#dce1ff)] transition-all hover:bg-white/5"
            >
              Back to Home
            </button>
          </div>

          <button
            onClick={() => this.setState({ showDetails: !showDetails })}
            className="text-xs text-[var(--text-primary,#dce1ff)]/50 underline mb-3"
          >
            {showDetails ? 'Hide' : 'Show'} technical details
          </button>

          {showDetails && (
            <div className="w-full max-w-2xl text-left">
              <div className="rounded-lg bg-black/40 border border-white/10 p-3 mb-2">
                <p className="text-xs font-mono text-red-300 break-words whitespace-pre-wrap">
                  {message}
                </p>
              </div>
              {stack && (
                <details className="mb-2">
                  <summary className="text-xs text-[var(--text-primary,#dce1ff)]/60 cursor-pointer">
                    JS stack
                  </summary>
                  <pre className="mt-2 text-[10px] font-mono text-[var(--text-primary,#dce1ff)]/60 bg-black/40 border border-white/10 rounded-lg p-3 overflow-auto max-h-60 whitespace-pre-wrap break-words">
                    {stack}
                  </pre>
                </details>
              )}
              {componentStack && (
                <details>
                  <summary className="text-xs text-[var(--text-primary,#dce1ff)]/60 cursor-pointer">
                    React component stack
                  </summary>
                  <pre className="mt-2 text-[10px] font-mono text-[var(--text-primary,#dce1ff)]/60 bg-black/40 border border-white/10 rounded-lg p-3 overflow-auto max-h-60 whitespace-pre-wrap break-words">
                    {componentStack}
                  </pre>
                </details>
              )}
              <button
                onClick={() => {
                  const text = `${message}\n\n${stack}\n\n${componentStack}`
                  navigator.clipboard?.writeText(text)
                }}
                className="mt-3 text-xs text-[var(--gold,#e9c349)] underline"
              >
                Copy details to clipboard
              </button>
            </div>
          )}
        </div>
      )
    }
    return this.props.children
  }
}
